import cn from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';
import { clamp, max, min } from 'utils/numbers';
import { block } from '../../utils';
import DragHandle, { DragHandleProps } from './DragHandle';
import { useTrackAssetContext } from './TrackAssetContext';

export type TrackAssetDragHandlesProps = {
  handleClassName?: string;
  resizable?: boolean;
};

export type ResizingHandle = 'left' | 'right';

export type ResizeCallback = (
  width: number,
  handle: ResizingHandle,
  pos: number,
) => void;

export const DRAG_HANDLE_WIDTH_PX = 11;

const TrackAssetDragHandles = (props: TrackAssetDragHandlesProps) => {
  const { handleClassName, resizable } = props;

  const {
    dragBounds,
    handleResize,
    handleResizeStart,
    handleResizeStop,
    id,
    position,
    pxPerSec,
    sizeBounds,
    width,
  } = useTrackAssetContext();

  const [activeHandle, setActiveHandle] = useState<ResizingHandle | undefined>(
    undefined,
  );

  const left = position.x;
  const right = left + width;

  const bounds = useMemo(
    () => ({
      maxPos: dragBounds.right,
      maxWidth: sizeBounds.maxWidth,
      minPos: dragBounds.left,
      minWidth: sizeBounds.minWidth,
    }),
    [
      dragBounds.left,
      dragBounds.right,
      sizeBounds.maxWidth,
      sizeBounds.minWidth,
    ],
  );

  const handleItemBeginningDrag: DragHandleProps['onDrag'] = useCallback(
    data => {
      const { minPos, maxWidth, minWidth } = bounds;

      const adjustedMinWidth = max(minWidth, 2 * DRAG_HANDLE_WIDTH_PX);
      const leftLimit = max(minPos, right - maxWidth);
      const rightLimit = right - adjustedMinWidth;

      const newPos = clamp(left + data.x, leftLimit, rightLimit);
      const newWidth = right - newPos;

      handleResize(newWidth, 'left', newPos);
    },
    [handleResize, bounds, left, right],
  );

  const handleItemBeginningDragStart: DragHandleProps['onStart'] = useCallback(
    (__, e) => {
      e.stopPropagation();
      handleResizeStart(width, 'left', left);
      setActiveHandle('left');
    },
    [handleResizeStart, left, width],
  );

  const handleItemBeginningDragStop: DragHandleProps['onStop'] = () => {
    handleResizeStop(width, 'left', left);
    setActiveHandle(undefined);
  };

  const handleItemEndDrag: DragHandleProps['onDrag'] = useCallback(
    data => {
      const { maxPos, maxWidth, minWidth } = bounds;

      const adjustedMinWidth = max(minWidth, 2 * DRAG_HANDLE_WIDTH_PX);
      const leftLimit = left + adjustedMinWidth - DRAG_HANDLE_WIDTH_PX;
      const rightLimit = min(maxPos, left + maxWidth) - DRAG_HANDLE_WIDTH_PX;

      const newPos = clamp(left + data.x, leftLimit, rightLimit);

      const newWidth = newPos - left + DRAG_HANDLE_WIDTH_PX;

      handleResize(newWidth, 'right', left);
    },
    [bounds, handleResize, left],
  );

  const handleItemEndDragStart: DragHandleProps['onStart'] = useCallback(
    (_, e) => {
      e.stopPropagation();
      handleResizeStart(width, 'right', left);
      setActiveHandle('right');
    },
    [handleResizeStart, left, width],
  );

  const handleItemEndDragStop: DragHandleProps['onStop'] = useCallback(() => {
    handleResizeStop(width, 'right', left);
    setActiveHandle(undefined);
  }, [handleResizeStop, left, width]);

  return (
    <div className={block('track-asset-handles')}>
      <DragHandle
        className={cn(block('track-asset-handle-left'), handleClassName)}
        displayX={left}
        id={`track-${id}-asset-handle-left`}
        showTooltip={resizable && activeHandle === 'left'}
        onDrag={handleItemBeginningDrag}
        onStart={handleItemBeginningDragStart}
        onStop={handleItemBeginningDragStop}
        pxPerSec={pxPerSec}
        width={DRAG_HANDLE_WIDTH_PX}
        x={0}
      />
      <DragHandle
        className={cn(block('track-asset-handle-right'), handleClassName)}
        displayX={right}
        id={`track-${id}-asset-handle-right`}
        showTooltip={resizable && activeHandle === 'right'}
        onDrag={handleItemEndDrag}
        onStart={handleItemEndDragStart}
        onStop={handleItemEndDragStop}
        pxPerSec={pxPerSec}
        width={DRAG_HANDLE_WIDTH_PX}
        x={right - left - DRAG_HANDLE_WIDTH_PX}
      />
    </div>
  );
};

export default TrackAssetDragHandles;
